import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, Sparkles } from "lucide-react";

/**
 * Persistent bottom-of-viewport CTA bar.
 *
 * Mounts on service hub pages (/seo, /web-design, etc.) and on city/industry
 * spoke pages so visitors always have a one-tap path to a strategy call or
 * proof. Hidden until the user scrolls 600px (so it doesn't compete with the
 * hero CTAs above the fold) and on print.
 */
export function StickyCTABar() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 transition-all duration-300 print:hidden ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-full opacity-0"
      }`}
    >
      <div className="mx-auto max-w-6xl px-3 pb-3 lg:px-6 lg:pb-5">
        <div className="rounded-2xl border border-brand-gold/40 bg-brand-black/95 px-4 py-3 shadow-gold backdrop-blur lg:px-6 lg:py-4">
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="hidden items-center gap-2 text-sm font-bold text-brand-cream sm:flex">
              <Sparkles className="h-4 w-4 text-brand-gold" />
              Ready to grow? Talk To The Owner Directly.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-gradient-gold px-5 py-2.5 text-sm font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4" />
                Get a Free Strategy Call
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/reviews"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-smooth hover:bg-white/10"
              >
                See Our Work
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
